import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["bordered", "children", "defaultSelected", "fill", "hideBorders", "onSelectedChange", "selected", "use", "Wrapper", "fullHeightPanel", "panelStyles"];
import { cloneElement, Children, isValidElement, useState, useEffect } from 'react';
import styled, { useTheme } from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { ActionsCollapser } from '../../../components/ActionsCollapser';
import { TabsProvider } from './TabsProvider';
import devLogger from 'react-utils/devLogger';
import { useTabBarContext } from '../TabBar/TabBarProvider';
import { deepMerge } from '../../utils/deepMerge';

// Allows us to control for tab overflow
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TabsWrapper = styled.div.withConfig({
  displayName: "Tabs__TabsWrapper",
  componentId: "zh9hfq-0"
})(({
  $cssStyles,
  $fullHeightPanel
}) => {
  const defaultStyling = {
    display: 'flex',
    flexDirection: 'column',
    height: $fullHeightPanel ? '100%' : 'auto'
  };
  return Object.assign({}, defaultStyling, $cssStyles);
});
// Render the content of the active tab
const getActiveTabPanel = ({
  selectedTab,
  validTabNodes
}) => {
  for (let i = 0; i < validTabNodes.length; i++) {
    const child = validTabNodes[i];
    const {
      children: tabContent = null,
      tabId = null
    } = child && child.props ? child.props : {};
    if (tabId === selectedTab) {
      return tabContent;
    }
  }
  return null;
};
const TabPanel = styled.div.withConfig({
  displayName: "Tabs__TabPanel",
  componentId: "zh9hfq-1"
})(({
  $cssStyles,
  $bordered,
  theme,
  $use,
  $fullHeightPanel,
  $styleOverrides = {}
}) => {
  return deepMerge({
    backgroundColor: $use === 'enclosed-shaded' ? theme.color['fill-surface-raised'] : 'transparent',
    paddingBlockStart: theme.space['400'],
    paddingBlockEnd: theme.space['400'],
    paddingInlineStart: $use === 'header' ? theme.space['1200'] : $use === 'default' ? theme.space['400'] : theme.space['0'],
    paddingInlineEnd: $use === 'default' ? theme.space['400'] : theme.space['0'],
    '&:empty': {
      padding: theme.space['0']
    },
    borderBlockStart: $bordered && $use === 'default' || $use !== 'default' ? `${theme.space['25']} solid` : 'none',
    borderBlockStartColor: $bordered && $use === 'default' ? theme.color['border-core-default'] : $use !== 'default' ? theme.color['border-core-default'] : 'transparent',
    flex: $fullHeightPanel ? 1 : 'none',
    minHeight: $fullHeightPanel ? 0 : 'auto',
    '&&': Object.assign({}, $cssStyles)
  }, $styleOverrides);
});

// Default wrapper for Tabs, when the Wrapper prop is provided it will be used instead
const StyledWrapper = styled.div.withConfig({
  displayName: "Tabs__StyledWrapper",
  componentId: "zh9hfq-2"
})([""]);
export const Tabs = _ref => {
  var _tabBarContext$tabsSt, _tabBarContext$tabPan;
  let {
      bordered = true,
      children,
      defaultSelected,
      fill,
      hideBorders,
      onSelectedChange,
      selected,
      use = 'default',
      Wrapper = StyledWrapper,
      fullHeightPanel = false,
      panelStyles
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Filter and map only valid Tab components
  const childrenArr = Children.toArray(children);
  const starterTabNodes = childrenArr.filter(child => /*#__PURE__*/isValidElement(child));
  const [validTabNodes, setValidTabNodes] = useState(starterTabNodes);

  // Set the default tab to be the default tab or the first non-disabled tab.
  const getDefaultTab = () => {
    if (defaultSelected === undefined) {
      return undefined;
    }

    // Allow disabled tab to be default selected
    const selectedTab = validTabNodes.find(child => /*#__PURE__*/isValidElement(child) && child.props.tabId === defaultSelected);
    if (selectedTab) return selectedTab;
  };
  const defaultTab = getDefaultTab();
  const [selectedTab, setSelectedTab] = useState( /*#__PURE__*/isValidElement(defaultTab) && defaultTab.props.tabId !== undefined ? defaultTab.props.tabId : '');
  const activeTabPanel = getActiveTabPanel({
    selectedTab,
    validTabNodes
  });
  const controlled = typeof selected === 'number' || typeof selected === 'string';
  const selectTab = (_event, tabId) => {
    setSelectedTab(tabId);
    if (onSelectedChange) {
      onSelectedChange(tabId);
    }
  };
  useEffect(() => {
    const newChildren = Children.toArray(children).filter(child => /*#__PURE__*/isValidElement(child));
    // Update existing tabs with new props while maintaining order
    setValidTabNodes(newChildren);
  }, [children]);

  // Check to see if the component is controlled, if so, set the selected tab to the controlled value
  useEffect(() => {
    if (controlled && selected !== selectedTab) {
      setSelectedTab(selected);
    }
  }, [controlled, selected, selectedTab]);
  useEffect(() => {
    // Check that all tabs have a unique tabId
    const tabIds = validTabNodes.map(node => node.props.tabId);
    const uniqueTabIds = new Set(tabIds);
    if (uniqueTabIds.size !== tabIds.length) {
      const counts = {};
      tabIds.forEach(id => {
        if (id !== undefined) {
          counts[id] = (counts[id] || 0) + 1;
        }
      });
      const duplicateIds = Object.keys(counts).filter(id => counts[id] > 1);
      devLogger.warn({
        message: `Duplicate tabIds detected: ${duplicateIds.join(', ')}`,
        key: 'TabsComponent:duplicate-tabIds'
      });
    }
  }, [validTabNodes]);

  // Render tabs and render title as main content for use in Tabs
  const tabs = validTabNodes.map((child, i) => {
    var _child$props;
    const {
      active,
      tabId
    } = (_child$props = child.props) !== null && _child$props !== void 0 ? _child$props : {};
    return /*#__PURE__*/cloneElement(child, Object.assign({}, child.props, {
      key: i,
      // this is necessary to find the matching content later
      active: active != null ? active : tabId === selectedTab,
      onSelectedChange: (event, id) => selectTab(event, id),
      use,
      fill,
      bordered,
      hideBorders,
      children: child.props.title // needed for the overflow dropdown labels
    }));
  });
  function dropDownClick(id) {
    // Extract the actual tabId from the React key by finding the tabId of the tab at the index of the key
    const actionId = typeof id === 'object' ? id.value : id;
    const cleanId = String(actionId).replace(/^\.\$\.\$/, ''); // Remove React key prefix
    const numId = isNaN(Number(cleanId)) ? cleanId : Number(cleanId);
    selectTab({}, numId);
    const tabToSelect = validTabNodes[Number(numId)];
    if (tabToSelect) {
      if (tabToSelect && tabToSelect.props.tabId !== undefined) {
        selectTab({}, tabToSelect.props.tabId);
      }
    }
    // Rearrange tabs such that the active tab is now at index 0
    if (Number(numId) > 0) {
      const currentTabNodes = validTabNodes;
      const [activeTab] = currentTabNodes.splice(Number(numId), 1);
      currentTabNodes.unshift(activeTab);
      setValidTabNodes(currentTabNodes);
    }
  }
  const theme = useTheme();

  // Style overrides for ActionsCollapser
  const actionsMenuStyleOverrides = {
    blockSize: 'auto',
    padding: theme.space['100'],
    // When tabs are enclosed, add a border to the dropdown
    border: use !== 'default' ? `${theme.color['border-core-default']} ${theme.space['25']} solid` : 'none',
    borderBlockEnd: '0',
    borderInlineStart: '0',
    borderTopRightRadius: theme.borderRadius['100'],
    width: 'fit-content'
  };
  const buttonWrapperStyleOverrides = {
    paddingInlineStart: use === 'header' ? theme.space['1200'] : 'undefined'
  };
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const tabBarContext = useTabBarContext();
  const mergedStyles = deepMerge(cssStyles, (_tabBarContext$tabsSt = tabBarContext === null || tabBarContext === void 0 ? void 0 : tabBarContext.tabsStyleOverrides) !== null && _tabBarContext$tabsSt !== void 0 ? _tabBarContext$tabsSt : {});
  const componentRole = activeTabPanel ? 'tablist' : 'navigation';
  const tabPanelStyleOverrides = (_tabBarContext$tabPan = tabBarContext === null || tabBarContext === void 0 ? void 0 : tabBarContext.tabPanelStyleOverrides) !== null && _tabBarContext$tabPan !== void 0 ? _tabBarContext$tabPan : {};
  const tabPanelCssStyles = useMapTokenPropsToCSS(panelStyles !== null && panelStyles !== void 0 ? panelStyles : {});
  return /*#__PURE__*/_jsx(TabsWrapper, Object.assign({
    $cssStyles: mergedStyles,
    $fullHeightPanel: fullHeightPanel
  }, domProps, {
    children: /*#__PURE__*/_jsxs(TabsProvider, {
      buttonWrapperStyleOverrides: buttonWrapperStyleOverrides,
      children: [/*#__PURE__*/_jsx(Wrapper, {
        children: /*#__PURE__*/_jsx(TabsProvider, {
          actionsMenuStyleOverrides: actionsMenuStyleOverrides,
          children: /*#__PURE__*/_jsx(ActionsCollapser, {
            role: componentRole,
            actionsMenuProps: {
              use: 'transparent',
              onActionClick: id => {
                dropDownClick(id);
              }
            },
            buttonWrapperProps: Object.assign({
              horizontalGap: 0
            }, tabBarContext === null || tabBarContext === void 0 ? void 0 : tabBarContext.buttonWrapperStyleOverrides),
            children: tabs
          })
        })
      }), /*#__PURE__*/_jsx(TabPanel, {
        $styleOverrides: tabPanelStyleOverrides,
        $cssStyles: tabPanelCssStyles,
        $bordered: bordered,
        $use: use,
        role: "tabpanel",
        $fullHeightPanel: fullHeightPanel,
        children: activeTabPanel
      })]
    })
  }));
};